import { useEffect } from 'react';

interface GoogleAnalyticsProps {
  measurementId?: string;
}

const GoogleAnalytics = ({ measurementId = 'G-XXXXXXXXXX' }: GoogleAnalyticsProps) => {
  useEffect(() => {
    // Check cookie preferences
    const checkConsent = () => {
      const preferencesStr = localStorage.getItem('cookiePreferences');
      
      if (!preferencesStr) {
        return false;
      }

      try {
        const preferences = JSON.parse(preferencesStr);
        return preferences.analytics === true;
      } catch (e) {
        console.error('Failed to parse cookie preferences');
        return false;
      }
    };

    const loadGoogleAnalytics = () => {
      if (!measurementId || measurementId === 'G-XXXXXXXXXX') {
        console.warn('Google Analytics measurement ID not configured');
        return;
      }

      // Check if already loaded
      if (document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`)) {
        return;
      }

      // Load gtag.js script
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      document.head.appendChild(script);

      // Initialize gtag
      script.onload = () => {
        window.dataLayer = window.dataLayer || [];
        function gtag(...args: any[]) {
          window.dataLayer.push(args);
        }
        gtag('js', new Date());
        gtag('config', measurementId, {
          anonymize_ip: true,
          cookie_flags: 'SameSite=None;Secure'
        });
      };
    };

    // Initial check
    if (checkConsent()) {
      loadGoogleAnalytics();
    }

    // Listen for storage changes (when user updates preferences)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'cookiePreferences') {
        if (checkConsent()) {
          loadGoogleAnalytics();
        } else {
          // Disable GA if consent is removed
          if (window.gtag) {
            window.gtag('consent', 'update', {
              analytics_storage: 'denied'
            });
          }
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [measurementId]);

  return null;
};

// Extend Window interface for TypeScript
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

export default GoogleAnalytics;
